import type { PostActivity as Activity } from '@reelplicate/api-client/model';
import { cn } from '@reelplicate/ui';
import { Fragment } from 'react';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Bar fills: done days (up to today) vs days still to come. */
const BAR: Record<'past' | 'future', Record<Activity, string>> = {
  past: { posted: 'bg-brand-primary', none: 'bg-bg-muted', scheduled: 'bg-bg-muted' },
  future: {
    posted: 'bg-brand-primary',
    none: 'bg-bg-subtle',
    scheduled: 'bg-brand-subtle border-brand-primary border-[1.5px]',
  },
};

const LEGEND = [
  { label: 'Posted', className: 'bg-brand-primary' },
  { label: 'No post', className: 'bg-bg-muted' },
  {
    label: 'Scheduled',
    className: 'bg-brand-subtle border-brand-primary border-[1.5px]',
  },
] as const;

function describe(activity: Activity[]): string {
  const days = (kind: Activity) => DAYS.filter((_, i) => activity[i] === kind);
  const posted = days('posted');
  const scheduled = days('scheduled');
  return [
    posted.length ? `Posted ${posted.join(', ')}` : 'No posts yet this week',
    scheduled.length ? `scheduled ${scheduled.join(', ')}` : 'nothing scheduled',
  ].join('; ');
}

/**
 * Figma "Post activity" (171:335): this week's days as 14×24 bars, split by
 * a rule after today. Posted / no post before it, scheduled / free after.
 */
export function PostActivity({
  activity,
  todayIndex,
}: {
  activity: Activity[];
  /** Today's place in the week (0 = Monday). */
  todayIndex: number;
}) {
  return (
    <div
      role="img"
      aria-label={describe(activity)}
      className="flex items-center gap-[5px]"
    >
      {activity.map((kind, i) => (
        <Fragment key={DAYS[i]}>
          <span
            className={cn(
              'h-6 w-3.5 shrink-0 rounded-xs',
              BAR[i <= todayIndex ? 'past' : 'future'][kind],
            )}
          />
          {i === todayIndex && <span className="bg-border-strong h-8 w-px shrink-0" />}
        </Fragment>
      ))}
    </div>
  );
}

/** The day letters under the "Post activity" header, today bold with a dot. */
export function PostActivityDays({ todayIndex }: { todayIndex: number }) {
  return (
    <span aria-hidden className="flex items-start gap-[5px]">
      {DAYS.map((day, i) => (
        <Fragment key={day}>
          <span
            className={cn(
              'flex w-3.5 flex-col items-center gap-px text-[10px] leading-4',
              i === todayIndex
                ? 'text-text-primary font-bold'
                : 'text-text-tertiary font-medium',
            )}
          >
            {day[0]}
            {i === todayIndex && <span className="bg-text-primary size-1 rounded-full" />}
          </span>
          {i === todayIndex && <span className="w-px shrink-0" />}
        </Fragment>
      ))}
    </span>
  );
}

/** The legend in the card header (Figma 171:243 – 171:249). */
export function PostActivityLegend() {
  return LEGEND.map(({ label, className }) => (
    <span key={label} className="flex items-center gap-1.5">
      <span aria-hidden className={cn('h-3.5 w-2 rounded-[2px]', className)} />
      <span className="text-caption text-text-secondary">{label}</span>
    </span>
  ));
}
